import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import type { Customer } from '../../types';
import { Button } from '@/components/ui/Button';
import { DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

// Zod schema for validation
const customerFormSchema = z.object({
  name: z.string().min(2, { message: "Το όνομα πρέπει να είναι τουλάχιστον 2 χαρακτήρες." }),
  phone: z.string().min(10, { message: "Ο αριθμός τηλεφώνου πρέπει να είναι τουλάχιστον 10 ψηφία." }),
  notes: z.string().optional(),
});

type CustomerFormData = z.infer<typeof customerFormSchema>;

interface CustomerFormProps {
  customer?: Customer | null;
  onSave: (data: CustomerFormData) => void;
  onCancel: () => void;
  isSaving: boolean;
  error?: string | null;
}

export const CustomerForm: React.FC<CustomerFormProps> = ({ customer, onSave, onCancel, isSaving, error }) => {
  const { register, handleSubmit, formState: { errors } } = useForm<CustomerFormData>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: {
      name: customer?.name || '',
      phone: customer?.phone || '',
      notes: customer?.notes || '',
    },
  });

  return (
    <form onSubmit={handleSubmit(onSave)}>
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-800">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          </div>
        </div>
      )}
      <div className="grid gap-4 py-4">
        <div>
          <Label htmlFor="name" className="text-right">Όνομα</Label>
          <input id="name" {...register('name')} className="mt-1 block w-full border border-border rounded-md shadow-sm py-3 px-4 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm touch-target mobile-touch-feedback bg-background text-foreground" />
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
        </div>
        <div>
          <Label htmlFor="phone" className="text-right">Τηλέφωνο</Label>
          <input id="phone" {...register('phone')} className="mt-1 block w-full border border-border rounded-md shadow-sm py-3 px-4 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm touch-target mobile-touch-feedback bg-background text-foreground" />
          {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
        </div>
        <div>
          <Label htmlFor="notes" className="text-right">Σημειώσεις</Label>
          <textarea id="notes" {...register('notes')} rows={3} className="mt-1 block w-full border border-border rounded-md shadow-sm py-3 px-4 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm touch-target mobile-touch-feedback bg-background text-foreground" />
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>Ακύρωση</Button>
        <Button type="submit" disabled={isSaving}>
          {isSaving ? 'Αποθήκευση...' : 'Αποθήκευση'}
        </Button>
      </DialogFooter>
    </form>
  );
};
